import React, { useState } from "react";
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { setFormModal } from "../redux/slices/modalSlice";
import { MemberUpgradeFormSchema } from "../utils/formValidationSchema";
import { BASE_URL } from "../config/settings";
import httpRequest from "../utils/httpRequest";
import { setloadTost } from "../redux/slices/toastSlice";
import { fetchMembershipUpgradeIndividual } from "../redux/slices/membershipUpgradeSlice";

const IndividualUpgradeForm = ({ memberProfile }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const errorsArray = Array.isArray(error) ? error : [error];

  const initialValues = {
    memberNo: memberProfile?.membershipId || "",
    newMembershipUpgrade: memberProfile?.nextMemberCategory || "",
    school: "",
    courseOfStudy: "",
    yearOfGraduation: "",
    qualification: "",
    certificate: null,
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError("");
    try {
      const file = values.certificate;
      const reader = new FileReader();

      reader.onloadend = async () => {
        const base64Data = reader.result.split(",")[1];

        const payload = {
          memberNo: memberProfile?.membershipId,
          school: values.school,
          courseOfStudy: values.courseOfStudy,
          yearOfGraduation: values.yearOfGraduation,
          qualification: values.qualification,
          certificate: {
            extension: "." + file.name.split(".").pop(),
            data: base64Data,
          },
        };

        const response = await httpRequest(
          `${BASE_URL}/v2/MembershipUpgrade/individual-application`,
          "post",
          payload
        );

        if (response?.isSuccess === true) {
          setLoading(false);
          dispatch(fetchMembershipUpgradeIndividual());
          dispatch(setFormModal({ status: false, data: null }));
          dispatch(
            setloadTost({
              title: "Member Upgrade",
              description: `Member upgraded successfully`,
              status: "success",
              duration: 5000,
              isClosable: true,
              position: "top",
            })
          );
        } else {
          setLoading(false);
          setError(
            response?.errors?.map((err) => err) ||
              response?.error?.description ||
              "Failed to upgrade member"
          );
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      setError(
        err?.payload?.message ||
          "Failed to upgrade member, please check your network"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <Alert status="error" className="mt-3" mb={3}>
          <AlertIcon />
          <AlertTitle>{errorsArray.join(", ")}</AlertTitle>
        </Alert>
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={MemberUpgradeFormSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          values,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => (
          <Form>
            {[
              { name: "memberNo", label: "Member No", readOnly: true },
              { name: "school", label: "Institution" },
              { name: "courseOfStudy", label: "Course Of Study" },
              { name: "yearOfGraduation", label: "Year Of Graduation" },
              { name: "qualification", label: "Qualification Obtained" },
            ].map(({ name, label, readOnly }) => (
              <FormControl
                key={name}
                mt={4}
                isInvalid={errors[name] && touched[name]}
                color="#121212"
              >
                <FormLabel htmlFor={name} fontSize={"16px"}>
                  {label}
                </FormLabel>
                <Input
                  fontSize={"small"}
                  bg={"#F3F3F3"}
                  name={name}
                  focusBorderColor="#ffffff"
                  borderRadius={"0"}
                  borderColor={"#FFFFFF"}
                  _placeholder={{ color: "#B4B1B1" }}
                  _hover={{ borderColor: "#FFFFFF" }}
                  type={name === "yearOfGraduation" ? "number" : "text"}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values[name]}
                  readOnly={readOnly}
                />
                {touched[name] && errors[name] && (
                  <FormErrorMessage>{errors[name]}</FormErrorMessage>
                )}
              </FormControl>
            ))}

            <FormControl
              mt={4}
              isInvalid={errors.certificate && touched.certificate}
              color="#121212"
            >
              <FormLabel htmlFor="certificate" fontSize={"16px"}>
                Upload Certificate
              </FormLabel>
              <Input
                placeholder="Add Here"
                fontSize={"small"}
                bg={"#F3F3F3"}
                name="certificate"
                focusBorderColor="#ffffff"
                borderRadius={"0"}
                borderColor={"#FFFFFF"}
                _placeholder={{ color: "#B4B1B1" }}
                _hover={{ borderColor: "#FFFFFF" }}
                type="file"
                onBlur={handleBlur}
                onChange={(event) =>
                  setFieldValue("certificate", event.currentTarget.files[0])
                }
              />
              {touched.certificate && errors.certificate && (
                <FormErrorMessage>{errors.certificate}</FormErrorMessage>
              )}
            </FormControl>

            <Flex justifyContent={"center"} alignItems={"center"} gap={5}>
              <Button
                mt={10}
                fontSize={"14px"}
                fontWeight={"500"}
                lineHeight={"16px"}
                width={"118px"}
                background={"brand.primary"}
                _hover={{ background: " " }}
                color={"#FFFFFF"}
                type="submit"
                isLoading={loading}
                loadingText="Saving ..."
              >
                Submit
              </Button>
              <Button
                mt={10}
                fontSize={"14px"}
                fontWeight={"500"}
                lineHeight={"16px"}
                width={"118px"}
                background={""}
                _hover={{ background: " " }}
                color={"#8F9BB3"}
                onClick={() =>
                  dispatch(setFormModal({ status: false, data: null }))
                }
              >
                Cancel
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default IndividualUpgradeForm;
